import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { 
  User, Mail, Shield, Key, Lock, Save, Loader2, 
  Eye, EyeOff, Calendar, Building2, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import MFASettings from '../components/MFASettings';

export default function ProfilePage() {
  const { user, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  
  // Profile edit state
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setProfile(response.data);
      setNewName(response.data.name || '');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setSavingName(true);
    try {
      await axios.put(`${API}/auth/profile`, { name: newName.trim() });
      setProfile(prev => ({ ...prev, name: newName.trim() }));
      setEditingName(false);
      toast.success('Name updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Failed to update name:', error);
      toast.error(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="profile-page">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-slate-100">
          <User className="w-6 h-6 text-slate-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Profile Settings</h1>
          <p className="text-slate-600">Manage your account and security settings</p>
        </div>
      </div>

      {/* Profile Information */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
          <CardDescription className="text-slate-600">
            Your basic account information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Name Field */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex-1">
              <Label className="text-sm text-slate-600">Full Name</Label>
              {editingName ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="max-w-xs bg-white border-slate-300"
                    data-testid="name-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingName(false);
                      setNewName(profile?.name || '');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <p className="font-medium text-slate-900 mt-1">{profile?.name}</p>
              )}
            </div>
            {!editingName && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingName(true)}
                className="text-slate-600"
                data-testid="edit-name-btn"
              >
                Edit
              </Button>
            )}
          </div>

          {/* Email Field (Read-only) */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Address
            </Label>
            <p className="font-medium text-slate-900 mt-1">{profile?.email}</p>
          </div>

          {/* Role Field */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Role
            </Label>
            <p className="font-medium text-slate-900 mt-1 capitalize">{profile?.role?.replace('_', ' ')}</p>
          </div>

          {/* Organization Field */}
          {profile?.tenant_name && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm text-slate-600 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Organization
              </Label>
              <p className="font-medium text-slate-900 mt-1">{profile?.tenant_name}</p>
            </div>
          )}

          {/* Member Since */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="text-sm text-slate-600 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Member Since
            </Label>
            <p className="font-medium text-slate-900 mt-1">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Password
          </CardTitle>
          <CardDescription className="text-slate-600">
            Change your account password
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showPasswordForm ? (
            <div className="space-y-4 max-w-md">
              {/* Current Password */}
              <div>
                <Label htmlFor="current-password" className="text-slate-700">Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="bg-white border-slate-300 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <Label htmlFor="new-password" className="text-slate-700">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-white border-slate-300 pr-10"
                    placeholder="At least 6 characters"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirm-password" className="text-slate-700">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 bg-white border-slate-300"
                  data-testid="confirm-password-input"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="save-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="border-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Password</p>
                <p className="text-sm text-slate-600">Last changed: Never</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowPasswordForm(true)}
                className="border-slate-300 text-slate-700"
                data-testid="change-password-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MFA Section */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription className="text-slate-600">
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MFASettings />
        </CardContent>
      </Card>
    </div>
  );
}
